/**
 * Plan Comparison Data Tab
 * Import public health plan data from CMS, state exchanges, and benchmark sources
 *
 * Trust Marketplace - Plan Intelligence System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { getWeaveinApiUrl } from '@/src/config';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Database,
  Upload,
  Download,
  RefreshCw,
  Globe,
  Building2,
  CheckCircle2,
  AlertCircle,
  XCircle,
  FileText,
  Settings,
  Clock,
  TrendingUp,
  BarChart3,
  Search,
  Filter,
  ExternalLink,
  Link as LinkIcon
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface PlanComparisonSource {
  id: string;
  name: string;
  type: 'federal' | 'state' | 'benchmark';
  description: string;
  lastUpdated: string | null;
  totalPlans: number;
  statesCovered: number;
  status: 'active' | 'pending' | 'error';
  url?: string;
}

interface ImportedPlan {
  id: string;
  plan_id: string;
  plan_name: string;
  carrier_name: string;
  plan_type: string;
  metal_tier: string;
  state: string;
  monthly_premium: number;
  deductible: number;
  oop_max: number;
  hsa_eligible: boolean;
  data_source: string;
  imported_at: string;
}

interface ImportStats {
  total_plans: number;
  states_covered: number;
  carriers: number;
  last_updated: string | null;
  federal_plans: number;
  state_plans: number;
  benchmark_plans: number;
}

const PlanComparisonDataTab: React.FC = () => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('overview');

  // Data
  const [stats, setStats] = useState<ImportStats>({
    total_plans: 0,
    states_covered: 0,
    carriers: 0,
    last_updated: null,
    federal_plans: 0,
    state_plans: 0,
    benchmark_plans: 0
  });

  const [sources, setSources] = useState<PlanComparisonSource[]>([
    {
      id: 'cms-qhp',
      name: 'CMS QHP Landscape',
      type: 'federal',
      description: 'Federal marketplace Qualified Health Plans from healthcare.gov',
      lastUpdated: null,
      totalPlans: 0,
      statesCovered: 0,
      status: 'pending',
      url: 'https://www.healthcare.gov/health-and-dental-plan-datasets-for-researchers-and-issuers/'
    },
    {
      id: 'cms-puf',
      name: 'CMS Public Use Files',
      type: 'federal',
      description: 'Individual and family premiums, metal tiers, and plan attributes',
      lastUpdated: null,
      totalPlans: 0,
      statesCovered: 0,
      status: 'pending',
      url: 'https://data.cms.gov/'
    },
    {
      id: 'covered-ca',
      name: 'Covered California',
      type: 'state',
      description: 'California state exchange plan data',
      lastUpdated: null,
      totalPlans: 0,
      statesCovered: 1,
      status: 'pending',
      url: 'https://www.coveredca.com/'
    },
    {
      id: 'ny-soh',
      name: 'NY State of Health',
      type: 'state',
      description: 'New York state exchange plan data',
      lastUpdated: null,
      totalPlans: 0,
      statesCovered: 1,
      status: 'pending',
      url: 'https://nystateofhealth.ny.gov/'
    },
    {
      id: 'benchmark',
      name: 'Industry Benchmark Data',
      type: 'benchmark',
      description: 'Aggregated industry data for cost comparison and analysis',
      lastUpdated: null,
      totalPlans: 0,
      statesCovered: 0,
      status: 'pending'
    }
  ]);

  const [importedPlans, setImportedPlans] = useState<ImportedPlan[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedSource, setSelectedSource] = useState<string>('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // In a real implementation, fetch from API
      // const token = localStorage.getItem('auth_token');
      // const response = await fetch(getWeaveinApiUrl('/api/plan-comparison/stats'), {
      //   headers: { 'Authorization': `Bearer ${token}` }
      // });
      // const data = await response.json();
      // setStats(data);
    } catch (error) {
      console.error('Error fetching plan comparison data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !selectedSource) {
      alert('Please select a file and data source');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('source', selectedSource);

      // In real implementation:
      // await fetch(getWeaveinApiUrl('/api/plan-comparison/import'), {
      //   method: 'POST',
      //   headers: { 'Authorization': `Bearer ${token}` },
      //   body: formData
      // });

      alert('Import started successfully');
      setShowUploadModal(false);
      setSelectedFile(null);
      setSelectedSource('');
      fetchData();
    } catch (error) {
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const getSourceIcon = (type: string) => {
    switch (type) {
      case 'federal':
        return <Globe className="h-5 w-5 text-blue-600" />;
      case 'state':
        return <Building2 className="h-5 w-5 text-green-600" />;
      case 'benchmark':
        return <BarChart3 className="h-5 w-5 text-purple-600" />;
      default:
        return <Database className="h-5 w-5 text-gray-600" />;
    }
  };

  const getSourceBgColor = (type: string) => {
    switch (type) {
      case 'federal':
        return 'bg-blue-100';
      case 'state':
        return 'bg-green-100';
      case 'benchmark':
        return 'bg-purple-100';
      default:
        return 'bg-gray-100';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'pending':
        return <Badge variant="outline">No Data</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-800">Error</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('admin.plan_comparison_data', 'Plan Comparison Data')}
          </h1>
          <p className="text-gray-600 mt-1">
            Import and manage public health plan data for comparison features
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => fetchData()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowUploadModal(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import Data
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Database className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.total_plans.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Total Plans</div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <Globe className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.states_covered}</div>
              <div className="text-sm text-gray-500">States Covered</div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100">
              <Building2 className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.carriers}</div>
              <div className="text-sm text-gray-500">Carriers</div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-100">
              <Clock className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-900">
                {stats.last_updated ? fmt.short(stats.last_updated) : 'Never'}
              </div>
              <div className="text-sm text-gray-500">Last Updated</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList>
          <TabsTrigger value="overview">Data Sources</TabsTrigger>
          <TabsTrigger value="plans">Imported Plans</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Data Sources Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sources.map(source => (
              <Card key={source.id} className="p-4 border-2 hover:border-blue-300 transition-colors">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${getSourceBgColor(source.type)}`}>
                    {getSourceIcon(source.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-medium">{source.name}</h3>
                      {getStatusBadge(source.status)}
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      {source.description}
                    </p>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="outline" className="text-xs">
                        {source.type === 'federal' ? 'Federal' : source.type === 'state' ? 'State' : 'Benchmark'}
                      </Badge>
                      {source.totalPlans > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {source.totalPlans.toLocaleString()} plans
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          setSelectedSource(source.id);
                          setShowUploadModal(true);
                        }}
                      >
                        <Upload className="h-3 w-3 mr-1" />
                        Import
                      </Button>
                      {source.url && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(source.url, '_blank')}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Import Instructions */}
          <Card className="mt-6 p-4 bg-blue-50 border-blue-200">
            <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Import Instructions
            </h4>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Download CMS QHP Landscape data from healthcare.gov</li>
              <li>Export state exchange data in CSV format with plan details</li>
              <li>Ensure files include: plan ID, name, carrier, premiums, deductibles, OOP max</li>
              <li>Use the Integration Workbench for advanced field mapping</li>
              <li>Supported formats: CSV, XLSX, JSON</li>
            </ul>
            <Button
              variant="link"
              className="text-blue-700 p-0 h-auto mt-2"
              onClick={() => window.open('/admin/integrations', '_blank')}
            >
              <LinkIcon className="h-3 w-3 mr-1" />
              Open Integration Workbench
            </Button>
          </Card>
        </TabsContent>

        {/* Imported Plans Tab */}
        <TabsContent value="plans" className="mt-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Imported Plans</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>

            {importedPlans.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Database className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium mb-2">No plans imported yet</p>
                <p className="text-sm mb-4">Import plan data from public sources to enable comparison features</p>
                <Button onClick={() => setShowUploadModal(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import Plan Data
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-4">Plan Name</th>
                      <th className="text-left py-2 px-4">Carrier</th>
                      <th className="text-left py-2 px-4">Type</th>
                      <th className="text-left py-2 px-4">State</th>
                      <th className="text-right py-2 px-4">Premium</th>
                      <th className="text-right py-2 px-4">Deductible</th>
                      <th className="text-left py-2 px-4">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importedPlans.map(plan => (
                      <tr key={plan.id} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-4">{plan.plan_name}</td>
                        <td className="py-2 px-4">{plan.carrier_name}</td>
                        <td className="py-2 px-4">{plan.plan_type}</td>
                        <td className="py-2 px-4">{plan.state}</td>
                        <td className="py-2 px-4 text-right">${plan.monthly_premium}/mo</td>
                        <td className="py-2 px-4 text-right">${plan.deductible.toLocaleString()}</td>
                        <td className="py-2 px-4">{plan.data_source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-4">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Data Source Settings</h3>
            <div className="space-y-4">
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-2">Auto-refresh Schedule</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Configure automatic data refresh from public sources
                </p>
                <div className="flex gap-2">
                  <select className="border rounded px-3 py-2 text-sm">
                    <option value="manual">Manual Only</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                  <Button variant="outline">Save Schedule</Button>
                </div>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-2">Data Retention</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Configure how long to keep historical plan data
                </p>
                <div className="flex gap-2">
                  <select className="border rounded px-3 py-2 text-sm">
                    <option value="1">1 Year</option>
                    <option value="2">2 Years</option>
                    <option value="3">3 Years</option>
                    <option value="5">5 Years</option>
                  </select>
                  <Button variant="outline">Save Setting</Button>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Plan Data</DialogTitle>
            <DialogDescription>
              Upload CSV, XLSX, or JSON file with health plan data
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-2">Data Source *</label>
              <select
                value={selectedSource}
                onChange={(e) => setSelectedSource(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="">Select a source...</option>
                {sources.map(source => (
                  <option key={source.id} value={source.id}>
                    {source.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".csv,.xlsx,.json"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="hidden"
                id="plan-file-upload"
              />
              <label htmlFor="plan-file-upload" className="cursor-pointer">
                <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                {selectedFile ? (
                  <p className="text-sm font-medium">{selectedFile.name}</p>
                ) : (
                  <p className="text-sm text-gray-500">
                    Click to select or drag and drop
                  </p>
                )}
              </label>
            </div>
            <div className="text-xs text-muted-foreground">
              Required fields: plan_id, plan_name, carrier_name, plan_type, monthly_premium, deductible, oop_max, state
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleFileUpload} disabled={uploading || !selectedFile || !selectedSource}>
              {uploading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlanComparisonDataTab;
